// Slides related sagas

import { put, select } from 'redux-saga/effects';

// Services
import { ErrorCategory, handleError } from '@services/errorService';

// Api
import client from '@api';

// Actions
import { SetSlidesLoading, UpdatedMarbleUser } from '@actions/user';
import { SetRedirectControl } from '@actions/redirectControl';

// Graph
import { createLink, deleteLink, updateLink } from '@graphql/mutations/link';

// Utils
import modifyLinkObject from '@utils/modifyLinkObject';

// Types
import type { Link, Slide } from './types';

export function* SaveSlidesSaga(): Generator {
  try {
    yield put(SetSlidesLoading(true));
    const { slides, isOnboardingPassed, username, deletedSlides } =
      (yield select(
        (state: {
          Slides: { slides: Slide[]; deletedSlides: string[] };
          User: { onboardingPassed: boolean; username: string };
        }) => ({
          slides: state?.Slides?.slides,
          deletedSlides: state?.Slides?.deletedSlides,
          isOnboardingPassed: !!state?.User?.onboardingPassed,
          username: state?.User?.username,
        }),
      )) as {
        slides: Slide[];
        isOnboardingPassed: boolean;
        username: string;
        deletedSlides: string[];
      };

    const updatedSlides =
      slides &&
      slides?.length &&
      slides?.filter(({ status }) => status === 'update');

    const createdSlides =
      slides &&
      slides?.length &&
      slides?.filter(({ status }) => status === 'create');

    let newSlides: Link[] | undefined;

    if (deletedSlides && deletedSlides?.length) {
      const { data } = (yield client().mutate({
        mutation: deleteLink,
        variables: {
          input: { _ids: deletedSlides },
        },
      })) as { data: { deleteLink: Link[] } };

      newSlides = data?.deleteLink;
    }

    if (updatedSlides && updatedSlides?.length) {
      const { data } = (yield client().mutate({
        mutation: updateLink,
        variables: {
          input: updatedSlides.map((slide) => modifyLinkObject(slide)),
        },
      })) as { data: { updateLink: Link[] } };

      newSlides = data?.updateLink;
    }

    if (createdSlides && createdSlides?.length) {
      const { data } = (yield client().mutate({
        mutation: createLink,
        variables: {
          input: createdSlides.map((slide) => modifyLinkObject(slide)),
        },
      })) as { data: { createLink: Link[] } };

      newSlides = data?.createLink;
    }

    yield put(
      UpdatedMarbleUser({
        links: newSlides,
      }),
    );

    if (isOnboardingPassed) {
      yield put(SetRedirectControl(`/${username}`));
    } else {
      localStorage.setItem('onboardingPath', '/onboarding/profile');
      yield put(SetRedirectControl('/onboarding/profile'));
    }

    yield put(SetSlidesLoading(false));
  } catch (e) {
    yield put(SetSlidesLoading(false));
    handleError(e, { category: ErrorCategory.API });
  }
}
